<template>
  <button class="instrument-option" @click="emitOnSelect">
    <div class="instrument-option__information">
      <component :is="iconComponent.component" v-bind="iconComponent.props">
        <template #fallback>
          <InstrumentAvatarFallback
            :instrument-type="type"
            :instrument-symbol="symbol"
            size="extra-small"
          />
        </template>
      </component>
      <BaseText
        class="instrument-option__text"
        :title="optionLabel"
        size="md"
        >{{ optionLabel }}</BaseText
      >
    </div>
    <BaseIcon
      v-if="isSelected"
      icon="ic_check_simple"
      width="24px"
      height="24px"
    />
  </button>
</template>

<script lang="ts">
import { Component, Vue, Prop, Emit } from 'vue-property-decorator';

import {
  BaseText,
  BaseIcon,
  InitialAvatarImage,
  InitialAvatarIcon
} from '@warrenbrasil/nebraska-web';
import { InstrumentAvatarFallback } from '@/modules/trade/components/InstrumentAvatarFallback';

import { InstrumentType } from '@/modules/trade/types';
import { getInstrumentSymbolPreffix } from '@/modules/trade/helpers/instruments';
import { InstrumentSelectionOptions } from '../../types';

@Component({
  components: {
    InitialAvatarImage,
    BaseText,
    BaseIcon,
    InstrumentAvatarFallback
  }
})
export default class InstrumentOption extends Vue {
  @Prop({ type: String, required: true })
  readonly symbol!: string;

  @Prop({ type: String, required: false })
  readonly name?: string;

  @Prop({ type: String, required: false })
  readonly type?: InstrumentType;

  @Prop({ type: [String, Number], required: true })
  readonly value!: string | number;

  @Prop({ type: Boolean, required: true })
  readonly isSelected!: boolean;

  @Emit('on-select')
  emitOnSelect() {
    return this.value;
  }

  get optionLabel() {
    let label = this.symbol;
    if (this.name) {
      label += ` · ${this.name}`;
    }

    return label;
  }

  get iconComponent() {
    if (this.value === InstrumentSelectionOptions.All) {
      return {
        component: InitialAvatarIcon,
        props: {
          icon: 'BA0130',
          size: 'extra-small',
          class: ['instrument-option__logo', 'instrument-option__logo--neutral']
        }
      };
    }

    return {
      component: InitialAvatarImage,
      props: {
        image: getInstrumentSymbolPreffix(this.symbol),
        alt: '',
        class: 'instrument-option__logo',
        size: 'extra-small'
      }
    };
  }
}
</script>

<style lang="less" scoped>
.instrument-option {
  display: flex;
  width: 100%;
  box-sizing: border-box;
  justify-content: space-between;
  align-items: center;
  padding: @size-spacing-x400;
  border-radius: @size-spacing-x300;
  cursor: pointer;

  &__information {
    display: flex;
    align-items: center;
  }

  &__text {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
    white-space: normal;
    margin-right: @size-spacing-x150;
    text-align: left;
  }

  &__logo {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: @size-spacing-x350;

    &--neutral {
      color: @element-secondary;
      background-color: @background-primary;
    }
  }

  &:hover {
    background-color: @background-hover;
  }
}
</style>
